/** Bot page (V3Bot): Overview · Chart · Trades · Performance · Settings. Live numbers come from the AccountRoom
 *  socket; history from D1 through the API. */
import type { AccountLive, ExecutionSummary } from "@dukun/protocol";
import type { StrategySpec } from "@dukun/protocol/strategy.ts";
import { useMemo, useState } from "react";
import { Crew, CHARACTERS } from "../components/Crew.tsx";
import { CmdNote, DecisionDialog, useCommand } from "../components/Decision.tsx";
import { EquityChart } from "../components/EquityChart.tsx";
import { PriceChart } from "../components/PriceChart.tsx";
import { Shell } from "../components/Shell.tsx";
import { Empty, ErrorNote, Loading, Page, Segmented, Stat, StatusChip, Tabs } from "../components/ui.tsx";
import { api } from "../lib/api.ts";
import { invalidate, useAccountLive, useApi } from "../lib/data.ts";
import { describeAny, specCoins } from "../lib/describe.ts";
import { ago, coin, dateUtc, nextGrid, pct, signedUsd, timeUtc, until, usd, usd0 } from "../lib/format.ts";
import { navigate } from "../lib/router.tsx";
import type { Bot, ChartData, Fill, StrategyRow } from "../lib/types.ts";

type BotDetail = Bot & { spec: StrategySpec };
type Tab = "overview" | "chart" | "trades" | "performance" | "settings";

export function BotPage({ id, tab }: { id: string; tab: string }) {
  const { data, error, reload } = useApi<{ account: BotDetail; live: AccountLive }>(`/accounts/${id}`);
  const { live: socket, status } = useAccountLive(id);
  const bot = data?.account, live = socket ?? data?.live ?? null;
  const t = (["overview", "chart", "trades", "performance", "settings"].includes(tab) ? tab : "overview") as Tab;
  if (error) return <Shell crumb="Bots"><Page><ErrorNote error={error} /></Page></Shell>;
  if (!bot) return <Shell crumb="Bots"><Loading /></Shell>;
  const stale = status !== "open" || (live?.asOf ? Date.now() - live.asOf > 3 * 3_600_000 : false);
  return (
    <Shell crumb={<>Bots · {bot.name}</>} actions={status !== "open" ? <span role="status" className="text-[13px] text-muted">Reconnecting…</span> : null}>
      <Page width={1100}>
        <div className="flex flex-wrap items-center gap-4">
          <Crew character={bot.character} size={52} />
          <div className="min-w-0 flex-1">
            <div className="eyebrow">{bot.mode === "paper" ? "Paper bot" : "Live bot"} · {bot.strategyName}</div>
            <h1 className="h1 mt-1">{bot.name}</h1>
          </div>
          <StatusChip status={bot.status} mode={bot.mode} exchange={bot.exchange} />
        </div>
        <Tabs label="Bot sections" value={t} onChange={(v) => navigate(`/bots/${id}/${v === "overview" ? "" : v}`.replace(/\/$/, ""))}
          options={[["overview", "Overview"], ["chart", "Chart"], ["trades", "Trades"], ["performance", "Performance"], ["settings", "Settings"]]} />
        {t === "overview" && <Overview bot={bot} live={live} stale={stale} />}
        {t === "chart" && <BotChart bot={bot} />}
        {t === "trades" && <BotTrades bot={bot} />}
        {t === "performance" && <Performance bot={bot} />}
        {t === "settings" && <BotSettings bot={bot} onChanged={reload} />}
      </Page>
    </Shell>
  );
}

function SafetyMeter({ dd, stopAt }: { dd: number; stopAt: number }) {
  const steps = [[0.05, ""], [0.1, ""], [0.15, ""], [stopAt, " stop"]] as const;
  const frac = Math.min(dd / stopAt, 1);
  return (
    <div className="flex flex-col gap-2" title="Drawdown ladder: the bot trades smaller after each step and stops everything at the last one">
      <div className="relative h-2 rounded-full bg-soft" role="meter" aria-valuemin={0} aria-valuemax={stopAt * 100} aria-valuenow={dd * 100} aria-label="Safety meter: how far below its best">
        <div className={`absolute inset-y-0 left-0 rounded-full ${dd >= 0.05 ? "bg-warn" : "bg-ok"}`} style={{ width: `${Math.max(frac * 100, 1)}%` }} />
        {steps.map(([v]) => <span key={v} className="absolute -top-1 h-4 w-px bg-line-strong" style={{ left: `${(v / stopAt) * 100}%` }} />)}
      </div>
      <div className="relative h-4 text-[11px] text-muted">{steps.map(([v, l]) => <span key={v} className="absolute -translate-x-1/2 whitespace-nowrap" style={{ left: `${Math.min((v / stopAt) * 100, 94)}%` }}>−{Math.round(v * 100)}%{l}</span>)}</div>
      <div className="text-[12px] text-muted">Trades at 70%, 50% and 35% size after each step; closes everything at the stop.</div>
    </div>
  );
}

function Overview({ bot, live, stale }: { bot: BotDetail; live: AccountLive | null; stale: boolean }) {
  const { data: eq } = useApi<{ points: [number, number, number, number][] }>(`/accounts/${bot.id}/equity?from=${Math.floor((Date.now() - 30 * 86_400_000) / 3_600_000) * 3_600_000}`);
  // venue-backed bots (realistic paper, demo): the headline is what the account really holds
  const v = live?.venue?.equity ?? live?.equity ?? bot.last_equity ?? bot.starting_balance;
  const re = live?.recentEquity ?? [];
  const day = re.length > 24 ? v - re[re.length - 25]![1] : null;
  const dd = live?.drawdown ?? 0, inPlay = (live?.grossLev ?? 0) * v;
  const grid = nextGrid();
  if (!live?.asOf) return <Empty title={`${bot.name} starts at the next hourly close`}>The engine adds new bots on the hour. The first rebalance follows at {timeUtc(grid)} ({until(grid)}).</Empty>;
  return (
    <div className="flex flex-col gap-8">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(160px,100%),1fr))] gap-2.5">
        <Stat label="Value" value={usd(v)} hint={`as of ${timeUtc(live.asOf)}`} />
        <Stat label="Today" value={day === null ? "—" : signedUsd(day)} tone={day === null ? undefined : day >= 0 ? "up" : "down"} />
        <Stat label="Since start" value={pct((v / bot.starting_balance - 1) * 100)} tone={v >= bot.starting_balance ? "up" : "down"} />
        <Stat label="In play" value={usd0(inPlay)} hint="Gross exposure: total size of all bets" />
      </div>
      {stale && <div className="rounded-lg bg-warn-soft px-3 py-2 text-[14px] text-warn-ink">Numbers may be old: last update {ago(live.asOf + 3_600_000)}.</div>}
      {(bot.exchange === "binance-demo" || bot.exchange === "paper") && <VenueCard bot={bot} live={live} />}
      <section className="card flex flex-col gap-4 p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2"><h2 className="h2">Safety</h2><span className="text-[14px] text-muted">{(dd * 100).toFixed(1)}% below its best · trading at {Math.round((live.throttle ?? 1) * 100)}% size</span></div>
        <SafetyMeter dd={dd} stopAt={bot.spec.safety?.stopAt ?? 0.3} />
      </section>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(420px,100%),1fr))] gap-6">
        <section className="flex flex-col gap-3">
          <h2 className="h2">Bets <span className="font-normal text-muted">{live.positions.length}</span></h2>
          {!live.positions.length ? <div className="text-muted">No open bets. The next rebalance is {until(grid)}.</div> : (
            <div className="card overflow-x-auto">
              <table className="table">
                <thead><tr><th>Coin</th><th>Bet</th><th className="text-right">Size</th><th className="text-right" title="Carry: spot held against a futures short, earning funding">Carry</th>{live.positions.some((p) => p.hold) && <th className="text-right" title="BTC held while BTC trends up">BTC held</th>}</tr></thead>
                <tbody>{[...live.positions].sort((a, b) => Math.abs(b.momentum) - Math.abs(a.momentum)).map((p) => (
                  <tr key={p.symbol}>
                    <td className="font-medium">{coin(p.symbol)}</td>
                    <td>{p.momentum === 0 ? <span className="text-muted">—</span> : p.momentum > 0 ? <span className="up">Bet up</span> : <span className="down">Bet down</span>}</td>
                    <td className="num text-right">{p.momentum === 0 ? "—" : usd0(Math.abs(p.momentum))}</td>
                    <td className="num text-right">{p.carry ? usd0(p.carry) : "—"}</td>
                    {live.positions.some((q) => q.hold) && <td className="num text-right">{p.hold ? usd0(p.hold) : "—"}</td>}
                  </tr>))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <section className="flex flex-col gap-3">
          <h2 className="h2">Activity</h2>
          <div className="flex flex-col">
            {live.events.slice(0, 10).map((e, i) => (
              <div key={i} className="flex gap-3 border-b border-soft py-2.5 last:border-0">
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${e.severity === "critical" ? "bg-danger" : e.severity === "warn" ? "bg-warn" : "bg-line-strong"}`} aria-hidden="true" />
                <span className="flex-1 text-[14px]">{e.text}<span className="block text-[12px] text-muted">{ago(e.t)}</span></span>
              </div>
            ))}
            {!live.events.length && <div className="text-muted">Nothing yet.</div>}
          </div>
        </section>
      </div>
      {eq && eq.points.length > 2 && (
        <section className="card flex flex-col gap-2 p-5"><h2 className="h2">Last 30 days</h2>
          <EquityChart log={false} height={220} t={eq.points.map((p) => p[0])} series={[{ label: bot.name, v: eq.points.map((p) => p[1]), tone: "accent" }]} /></section>
      )}
      <section className="flex flex-col gap-2"><h2 className="h2">Rules</h2><ul className="m-0 flex list-disc flex-col gap-1 pl-5 text-[14px] leading-relaxed">{describeAny(bot.spec).map((l) => <li key={l}>{l}</li>)}</ul></section>
    </div>
  );
}

/** Venue-backed bots: the account (live-market paper fills or Binance Demo) next to the model, and what execution costs. */
function VenueCard({ bot, live }: { bot: BotDetail; live: AccountLive }) {
  const { data } = useApi<{ executions: ExecutionSummary[]; totals: { count: number; notional: number; costBps: number | null; makerPct: number | null; assumedBps: number | null } }>(`/accounts/${bot.id}/executions?limit=5`, { refreshMs: 60_000 });
  const v = live.venue, tot = data?.totals;
  return (
    <section className="card flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="h2">{bot.exchange === "binance-demo" ? "On Binance Demo" : "Fills on the live order book"}</h2>
        <span className="text-[13px] text-muted">{v ? `synced ${ago(v.t)}` : "waiting for the first sync"}</span>
      </div>
      {v ? (
        <>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(150px,100%),1fr))] gap-2.5">
            <Stat label={bot.exchange === "binance-demo" ? "Value on Binance" : "Value"} value={usd(v.equity)} />
            <Stat label="Backtest-perfect" value={usd(v.modelEquity)} hint="The same strategy filled at the hourly close with the backtest's costs" />
            <Stat label="Gap" value={pct(v.trackingPct, 2)} tone={v.trackingPct >= 0 ? "up" : "down"} hint="Exchange value vs the model: what fills, fees and timing cost" />
            <Stat label="Cost per trade" value={tot?.costBps != null ? `${tot.costBps.toFixed(1)} bps` : "—"} hint={`Fees + slippage vs the model's prices; the backtest assumes ${tot?.assumedBps?.toFixed(1) ?? "3.5"} bps`} />
          </div>
          {!!v.positions.length && (
            <div className="card overflow-x-auto">
              <table className="table"><thead><tr><th>Contract</th><th className="text-right">Holding</th><th className="text-right">Strategy wants</th><th className="text-right">Value</th></tr></thead>
                <tbody>{v.positions.map((p) => {
                  const off = Math.abs(p.qty - p.targetQty) > Math.max(Math.abs(p.targetQty) * 0.02, 1e-9);
                  return <tr key={`${p.market}:${p.symbol}`}><td className="font-medium">{coin(p.symbol)} <span className="text-[12px] text-muted">{p.market}</span></td>
                    <td className={`num text-right ${off ? "down" : ""}`}>{+p.qty.toPrecision(6)}</td><td className="num text-right">{+p.targetQty.toPrecision(6)}</td><td className="num text-right">{usd0(p.notional)}</td></tr>;
                })}</tbody></table>
            </div>
          )}
          {!!data?.executions.length && (
            <div className="flex flex-col gap-1.5">
              <div className="eyebrow">Recent executions</div>
              {data.executions.map((e) => (
                <div key={e.planId} className="flex flex-wrap gap-x-4 border-b border-soft py-1.5 text-[13px] last:border-0">
                  <span className="text-muted">{timeUtc(e.barTime + 3_600_000)}</span>
                  <span>{e.legs.length} orders · {usd0(e.notional)}</span>
                  <span className={e.costBps <= e.assumedBps ? "up" : "down"}>{e.costBps.toFixed(1)} bps</span>
                  <span className="text-muted">{Math.round(e.makerPct)}% maker · {Math.round((e.end - e.start) / 60_000)} min</span>
                  {e.legs.some((l) => l.error) && <span className="down">{e.legs.filter((l) => l.error).length} failed</span>}
                </div>
              ))}
            </div>
          )}
        </>
      ) : <div className="text-muted">{bot.exchange === "binance-demo" ? `The engine trades the demo account after each hourly close. Make sure the demo futures wallet holds at least ${usd0(bot.starting_balance)} USDT.` : "Orders are worked on the live order book after each rebalance; the first numbers appear after the next hourly close."}</div>}
    </section>
  );
}

function symbolsOf(spec: StrategySpec): string[] {
  return specCoins(spec).slice(0, 6);
}

function BotChart({ bot }: { bot: BotDetail }) {
  const syms = symbolsOf(bot.spec);
  const [sym, setSym] = useState(syms.includes("SOLUSDT") ? "SOLUSDT" : syms[0]!);
  const [tf, setTf] = useState<"1h" | "4h" | "8h" | "1D">("8h");
  const [days, setDays] = useState<"30" | "90" | "365">("90");
  const to = Math.floor(Date.now() / 3_600_000) * 3_600_000;
  const { data, error, loading } = useApi<ChartData>(`/charts/bots/${bot.id}/${sym}?tf=${tf}&from=${to - Number(days) * 86_400_000}&to=${to}`);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-[13px] text-muted">Coin<select className="input w-auto py-1.5" value={sym} onChange={(e) => setSym(e.target.value)}>{syms.map((s) => <option key={s} value={s}>{coin(s)}</option>)}</select></label>
        <Segmented label="Timeframe" value={tf} onChange={setTf} options={[["1h", "1h"], ["4h", "4h"], ["8h", "8h"], ["1D", "1D"]]} />
        <Segmented label="Window" value={days} onChange={setDays} options={[["30", "30d"], ["90", "90d"], ["365", "1y"]]} />
      </div>
      <ErrorNote error={error} />
      {loading ? <Loading /> : data && <div className="card p-3"><PriceChart data={data} height={480} /></div>}
      {data && <EventList data={data} />}
    </div>
  );
}

export function EventList({ data }: { data: ChartData }) {
  const ev = [...data.events].reverse();
  return (
    <section className="flex flex-col gap-2">
      <h2 className="h2">Trades on {coin(data.symbol)} <span className="font-normal text-muted">{data.events.length}</span></h2>
      <div className="card max-h-[420px] overflow-auto">
        <table className="table"><thead><tr><th>When</th><th>What</th><th className="text-right">Amount</th><th className="text-right">Price</th><th className="text-right">Position after</th></tr></thead>
          <tbody>{ev.map((e) => (
            <tr key={`${e.t}-${e.positionAfter}`}><td className="whitespace-nowrap text-muted">{timeUtc(e.t)}</td>
              <td><span className={e.side === "buy" ? "up" : "down"}>{{ long: "Opened long", short: "Opened short", close: "Closed", add: "Added", trim: "Trimmed" }[e.kind]}</span></td>
              <td className="num text-right">{usd0(Math.abs(e.notional))}</td><td className="num text-right">{e.price}</td><td className="num text-right">{usd0(e.positionAfter)}</td></tr>))}
          </tbody></table>
        {!ev.length && <div className="p-4 text-muted">No trades in this window.</div>}
      </div>
    </section>
  );
}

function BotTrades({ bot }: { bot: BotDetail }) {
  const [before, setBefore] = useState<number[]>([]);
  const cursor = before[before.length - 1];
  const { data, loading } = useApi<{ fills: Fill[] }>(`/accounts/${bot.id}/trades?limit=100${cursor ? `&before=${cursor}` : ""}`);
  return (
    <div className="flex flex-col gap-3">
      <FillTable fills={data?.fills ?? []} loading={loading} />
      <div className="flex gap-2">
        {before.length > 0 && <button className="btn" onClick={() => setBefore(before.slice(0, -1))}>Newer</button>}
        {(data?.fills.length ?? 0) === 100 && <button className="btn" onClick={() => setBefore([...before, data!.fills[data!.fills.length - 1]!.ts])}>Older</button>}
      </div>
    </div>
  );
}

/** Venue values on the model's hourly grid (carry the last known value forward; before the first sync use the model start). */
function alignTo(t: number[], series: [number, number][]): number[] {
  const out: number[] = []; let k = 0, last = series[0]?.[1] ?? NaN;
  for (const x of t) { while (k < series.length && series[k]![0] <= x) last = series[k++]![1]; out.push(last); }
  return out;
}

export function FillTable({ fills, loading, showBot = false }: { fills: Fill[]; loading: boolean; showBot?: boolean }) {
  if (loading) return <Loading />;
  if (!fills.length) return <Empty title="No trades yet">Trades appear after the first rebalance.</Empty>;
  return (
    <div className="card overflow-x-auto">
      <table className="table min-w-[720px]">
        <thead><tr><th>When</th>{showBot && <th>Bot</th>}<th>Coin</th><th>Book</th><th>Side</th><th className="text-right">Amount</th><th className="text-right">Price</th><th className="text-right">Fee</th><th>Why</th></tr></thead>
        <tbody>{fills.map((f) => (
          <tr key={f.id}>
            <td className="whitespace-nowrap text-muted">{timeUtc(f.ts)}</td>
            {showBot && <td className="flex items-center gap-1.5"><Crew character={f.character ?? "planet"} size={18} />{f.bot_name}</td>}
            <td className="font-medium">{coin(f.symbol)} <span className="text-[12px] text-muted">{f.market}</span></td>
            <td className="text-muted">{f.book === "carry" ? "Carry" : f.book === "hold" ? "BTC held" : "Momentum"}</td>
            <td className={f.side === "buy" ? "up" : "down"}>{f.side === "buy" ? "Buy" : "Sell"}</td>
            <td className="num text-right">{usd(f.notional)}</td><td className="num text-right">{f.price}</td><td className="num text-right">{usd(f.fee)}</td>
            <td className="text-[13px] text-muted">{f.reason}</td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

function Performance({ bot }: { bot: BotDetail }) {
  const { data, loading } = useApi<{ points: [number, number, number, number][]; venue?: [number, number][] }>(`/accounts/${bot.id}/equity?from=0`);
  const months = useMemo(() => {
    const out = new Map<string, [number, number]>();
    for (const [t, e] of data?.points ?? []) { const k = new Date(t).toISOString().slice(0, 7); const m = out.get(k); out.set(k, m ? [m[0], e] : [e, e]); }
    let prev: number | null = null;
    return [...out].map(([k, [a, b]]) => { const base = prev ?? a; prev = b; return { k, r: (b / base - 1) * 100 }; });
  }, [data]);
  if (loading) return <Loading />;
  const pts = data?.points ?? [];
  if (pts.length < 3) return <Empty title="Not enough history yet">Performance fills in as the bot trades.</Empty>;
  const maxDd = Math.max(...pts.map((p) => p[2]));
  const last = pts[pts.length - 1]!;
  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(160px,100%),1fr))] gap-2.5">
        <Stat label="Since start" value={pct((last[1] / bot.starting_balance - 1) * 100)} tone={last[1] >= bot.starting_balance ? "up" : "down"} />
        <Stat label="Worst drop" value={`−${(maxDd * 100).toFixed(1)}%`} tone="down" />
        <Stat label="Running for" value={`${Math.max(1, Math.round((last[0] - pts[0]![0]) / 86_400_000))} days`} />
      </div>
      <section className="card p-5"><EquityChart log={false} t={pts.map((p) => p[0])} series={[
        ...(data?.venue?.length ? [{ label: bot.exchange === "binance-demo" ? "On Binance Demo" : "Live order book fills", v: alignTo(pts.map((p) => p[0]), data.venue), tone: "accent" as const }] : []),
        { label: data?.venue?.length ? "Backtest-perfect" : bot.name, v: pts.map((p) => p[1]), tone: data?.venue?.length ? "muted" as const : "accent" as const },
      ]} /></section>
      <section className="flex flex-col gap-2"><h2 className="h2">Month by month</h2>
        <div className="card overflow-x-auto"><table className="table"><thead><tr><th>Month</th><th className="text-right">Return</th></tr></thead>
          <tbody>{[...months].reverse().map((m) => <tr key={m.k}><td>{m.k}</td><td className={`num text-right ${m.r >= 0 ? "up" : "down"}`}>{pct(m.r)}</td></tr>)}</tbody></table></div>
      </section>
      <p className="m-0 text-[12px] text-muted">Started {dateUtc(pts[0]![0])} with {usd(bot.starting_balance, 0)}.</p>
    </div>
  );
}

function BotSettings({ bot, onChanged }: { bot: BotDetail; onChanged: () => void }) {
  const cmd = useCommand(bot.id);
  const [dlg, setDlg] = useState<null | "pause" | "resume" | "flatten" | "risk" | "switch" | "admin_resume">(null);
  const [risk, setRisk] = useState(bot.risk_scale), [target, setTarget] = useState<string>("");
  const { data: strategies } = useApi<{ strategies: StrategyRow[] }>("/strategies");
  const [name, setName] = useState(bot.name), [saving, setSaving] = useState(false);
  const grid = nextGrid();
  const run = (body: Record<string, unknown>) => { setDlg(null); void cmd.send(body).then(onChanged); };
  return (
    <div className="flex max-w-[720px] flex-col gap-8">
      <CmdNote state={cmd.state} />
      <section className="card flex flex-col gap-3 p-5">
        <h2 className="h2">Running</h2>
        <p className="m-0 text-muted">Pausing stops new trades but keeps current bets and safety rules. Closing everything sells all bets at the next hourly close and pauses the bot.</p>
        <div className="flex flex-wrap gap-2">
          {bot.status === "active" && <button className="btn" onClick={() => setDlg("pause")}>Pause</button>}
          {bot.status === "paused" && <button className="btn-primary" onClick={() => setDlg("resume")}>Resume</button>}
          {bot.status === "halted" && <button className="btn-primary" onClick={() => setDlg("admin_resume")}>Restart after safety stop</button>}
          {(bot.status === "active" || bot.status === "paused") && <button className="btn-warn" onClick={() => setDlg("flatten")}>Close everything</button>}
        </div>
      </section>
      <section className="card flex flex-col gap-3 p-5">
        <h2 className="h2">Risk level</h2>
        <p className="m-0 text-muted">Trade at a share of the strategy's size. Lower means smaller swings and smaller gains.</p>
        <div className="flex flex-wrap items-center gap-3">
          <input type="range" min={0.1} max={1} step={0.05} value={risk} onChange={(e) => setRisk(Number(e.target.value))} className="w-60 accent-[rgb(var(--accent))]" aria-label="Risk level" />
          <span className="num">{Math.round(risk * 100)}%</span>
          <button className="btn" disabled={risk === bot.risk_scale} onClick={() => setDlg("risk")}>Apply</button>
        </div>
      </section>
      <section className="card flex flex-col gap-3 p-5">
        <h2 className="h2">Strategy</h2>
        <p className="m-0 text-muted">Now: {bot.strategyName}.{bot.pending_strategy_version_id ? " A switch is queued for the next rebalance." : ""}</p>
        <div className="flex flex-wrap gap-2">
          <select className="input w-auto" value={target} onChange={(e) => setTarget(e.target.value)} aria-label="Switch to strategy">
            <option value="">Choose a strategy…</option>
            {strategies?.strategies.filter((s) => s.version_id !== bot.strategy_version_id).map((s) => <option key={s.version_id} value={s.version_id}>{s.name}{s.kind === "user" ? " (mine)" : ""}</option>)}
          </select>
          <button className="btn" disabled={!target} onClick={() => setDlg("switch")}>Switch</button>
        </div>
      </section>
      <section className="card flex flex-col gap-3 p-5">
        <h2 className="h2">Name and character</h2>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1.5"><span className="text-[13px] text-muted">Name</span><input className="input w-56" value={name} maxLength={40} onChange={(e) => setName(e.target.value)} /></label>
          <button className="btn" disabled={saving || name.trim() === bot.name || !name.trim()} onClick={async () => { setSaving(true); await api(`/accounts/${bot.id}`, { method: "PATCH", body: { name } }); invalidate("/accounts"); setSaving(false); onChanged(); }}>Save</button>
        </div>
        <div className="flex flex-wrap gap-2">{CHARACTERS.map((c) => (
          <button key={c} aria-label={c} aria-pressed={bot.character === c} className={`rounded-xl border p-1.5 ${bot.character === c ? "border-accent bg-accent-soft" : "border-line"}`}
            onClick={async () => { await api(`/accounts/${bot.id}`, { method: "PATCH", body: { character: c } }); invalidate("/accounts"); onChanged(); }}><Crew character={c} size={32} /></button>))}
        </div>
      </section>

      <DecisionDialog open={dlg === "pause"} title={`Pause ${bot.name}?`} confirm="Pause" onConfirm={() => run({ type: "pause" })} onClose={() => setDlg(null)}
        what="No new trades. Current bets stay open and keep moving with the market; carry positions keep earning funding." when="Right away." tradeoff="If the market turns, the bot will not adjust until you resume. The safety stop still works." />
      <DecisionDialog open={dlg === "resume"} title={`Resume ${bot.name}?`} confirm="Resume" onConfirm={() => run({ type: "resume" })} onClose={() => setDlg(null)}
        what="The bot trades its strategy again." when={`Right away; the next rebalance is ${until(grid)}.`} tradeoff="Bets will be resized to the strategy's targets at the next rebalance." />
      <DecisionDialog open={dlg === "flatten"} tone="warn" title={`Close everything in ${bot.name}?`} confirm="Close everything" onConfirm={() => run({ type: "flatten" })} onClose={() => setDlg(null)}
        what="Sell every bet and unwind every carry position, then pause." when="At the next hourly close." tradeoff="You pay fees to exit and miss any rebound. Use Pause if you only want to stop new trades." />
      <DecisionDialog open={dlg === "admin_resume"} title={`Restart ${bot.name}?`} confirm="Restart" onConfirm={() => run({ type: "admin_resume" })} onClose={() => setDlg(null)}
        what="Clear the safety stop. Drawdown is measured again from today's value." when="Right away." tradeoff="The stop fired because the strategy lost 30% from its best. Make sure you still want to run it." />
      <DecisionDialog open={dlg === "risk"} title={`Set risk to ${Math.round(risk * 100)}%?`} confirm={`Set ${Math.round(risk * 100)}%`} onConfirm={() => run({ type: "set_risk_scale", riskScale: risk })} onClose={() => setDlg(null)}
        what={`Every bet becomes about ${Math.round(risk * 100)}% of what the strategy would hold.`} when={`At the next rebalance (${until(grid)}).`} tradeoff={risk < bot.risk_scale ? "Smaller swings and smaller gains." : "Bigger swings and deeper drops."} />
      <DecisionDialog open={dlg === "switch"} title="Switch strategy?" confirm="Switch" onConfirm={() => run({ type: "switch_strategy", strategyVersionId: target })} onClose={() => setDlg(null)}
        what={`Close all current bets and start ${strategies?.strategies.find((s) => s.version_id === target)?.name ?? "the new strategy"} with the bot's value.`} when={`At the next 8-hour rebalance (${until(grid)}).`} tradeoff="You pay fees to exit and re-enter; the safety meter restarts from the new strategy." />
    </div>
  );
}
